import type { Intersection, Object3D } from 'three'
import { Box3, Matrix3, Matrix4, Mesh, Raycaster, Vector3 } from 'three'
import type { TilesDisplaySnapshot } from '@/plugins/tiles/contracts'
import type { CompiledClipState } from '@/shared/clipping/CompiledClipState'
import { evaluateCompiledClipState } from '@/shared/clipping/CompiledClipState'
import type { PickQuery, PickResult } from '@/shared/types/core'

/** 无常驻索引的几何回退：包/primitive 包围体粗筛后只查询有效三角范围。 */
export function pickMeshTiles(
  snapshot: TilesDisplaySnapshot,
  query: PickQuery,
  clipState: CompiledClipState
): PickResult | null {
  if (query.gpuOnly || (query.kinds && !query.kinds.includes('tiles'))) return null
  if (query.ray.direction.lengthSq() === 0) return null
  const raycaster = new Raycaster()
  raycaster.ray.set(query.ray.origin, query.ray.direction.clone().normalize())
  raycaster.near = Math.max(0, query.minDistance ?? 0)
  raycaster.layers.mask = query.layerMask ?? query.camera.layers.mask
  const bounds = new Box3()
  const matrix = new Matrix4()
  let nearest: PickResult | null = null
  for (const item of snapshot.items) {
    const assetId = item.assetId ?? item.content.source.assetId
    if (query.assetIds && !query.assetIds.includes(assetId)) continue
    bounds.copy(item.content.boundingBox).applyMatrix4(item.matrixWorld)
    if (!raycaster.ray.intersectsBox(bounds)) continue
    const root = item.content.object3D
    root.updateMatrixWorld(true)
    const visit = (object: Object3D, path: string) => {
      if (!object.visible) return
      if (object instanceof Mesh && object.layers.test(raycaster.layers)) {
        matrix.multiplyMatrices(item.matrixWorld, object.matrixWorld)
        if (matrix.determinant() === 0) return
        // Three 的 Mesh.raycast 已遵守 side、groups 和 drawRange；代理借用原几何。
        const proxy = new Mesh(object.geometry, object.material)
        proxy.matrixWorld.copy(matrix)
        const intersections: Intersection[] = []
        proxy.raycast(raycaster, intersections)
        for (const hit of intersections) {
          const materialIndex = hit.face?.materialIndex ?? 0
          const material = Array.isArray(object.material)
            ? object.material[materialIndex]
            : object.material
          if (!material?.visible || evaluateCompiledClipState(clipState, hit.point).hidden) continue
          if (nearest && nearest.distance <= hit.distance) continue
          const normal = hit.face?.normal
            .clone()
            .applyNormalMatrix(new Matrix3().getNormalMatrix(matrix))
          nearest = {
            assetId,
            kind: 'tiles',
            point: new Vector3().copy(hit.point),
            distance: hit.distance,
            normal,
            tilesSource: Object.freeze({
              ...item.content.source,
              assetId,
              recordGeneration: snapshot.recordGeneration,
              viewId: snapshot.viewId,
              commitVersion: snapshot.version,
              primitiveId:
                typeof object.userData.tilesSource?.id === 'string'
                  ? object.userData.tilesSource.id
                  : path,
              triangleIndex: hit.faceIndex ?? 0,
              materialIndex,
            }),
          }
        }
      }
      object.children.forEach((child, index) => visit(child, `${path}/${index}`))
    }
    visit(root, '0')
  }
  return nearest
}
